/**
 * Dashboard API (RTK Query) — Stride admin v2 endpoints only.
 */

import { createApi } from '@reduxjs/toolkit/query/react'
import { strideBaseQuery } from '@/services/rtkBaseQuery'
import {
  extractInterestList,
  mapAdminDashboardToDashboardData,
  mapInterestDto,
} from '@/services/adminResponseMappers'
import type { DashboardData, Interest } from '@/types/dashboard'

export type { DashboardData, DashboardStats, Interest } from '@/types/dashboard'

export const dashboardApi = createApi({
  reducerPath: 'dashboardApi',
  baseQuery: strideBaseQuery,
  tagTypes: ['Dashboard', 'Interest'],
  endpoints: builder => ({
    getDashboardData: builder.query<DashboardData, void>({
      query: () => '/admin/dashboard',
      transformResponse: (response: unknown) => mapAdminDashboardToDashboardData(response),
      providesTags: ['Dashboard'],
    }),
    getInterests: builder.query<Interest[], { page?: number; limit?: number } | void>({
      query: arg => ({
        url: '/admin/interests',
        params: {
          page: arg && typeof arg === 'object' ? arg.page ?? 0 : 0,
          limit: arg && typeof arg === 'object' ? arg.limit ?? 50 : 50,
        },
      }),
      transformResponse: (response: unknown) => extractInterestList(response),
      providesTags: ['Interest'],
    }),
    getInterestById: builder.query<Interest, string>({
      query: id => `/admin/interests/${encodeURIComponent(id)}`,
      transformResponse: (response: unknown) => mapInterestDto(response),
      providesTags: (_result, _error, id) => [{ type: 'Interest', id }],
    }),
    deleteInterest: builder.mutation<void, string>({
      query: id => ({
        url: `/admin/interests/${encodeURIComponent(id)}/deactivate`,
        method: 'PATCH',
      }),
      invalidatesTags: ['Interest'],
    }),
    createInterest: builder.mutation<
      Interest,
      { name: string; iconUrl?: string } | FormData
    >({
      query: payload => {
        if (payload instanceof FormData) {
          const name = String(payload.get('name') ?? '')
          const iconUrl = payload.get('iconUrl')
          return {
            url: '/admin/interests',
            method: 'POST',
            body: {
              name,
              ...(typeof iconUrl === 'string' && iconUrl ? { iconUrl } : {}),
            },
          }
        }
        return {
          url: '/admin/interests',
          method: 'POST',
          body: {
            name: payload.name,
            ...(payload.iconUrl ? { iconUrl: payload.iconUrl } : {}),
          },
        }
      },
      transformResponse: (response: unknown) => mapInterestDto(response),
      invalidatesTags: ['Interest'],
    }),
    updateInterest: builder.mutation<
      Interest,
      { id: string; name: string; iconUrl?: string }
    >({
      query: ({ id, name, iconUrl }) => ({
        url: `/admin/interests/${encodeURIComponent(id)}`,
        method: 'PATCH',
        body: {
          name,
          ...(iconUrl !== undefined ? { iconUrl } : {}),
        },
      }),
      transformResponse: (response: unknown) => mapInterestDto(response),
      invalidatesTags: ['Interest'],
    }),
  }),
})

export const {
  useGetDashboardDataQuery,
  useGetInterestsQuery,
  useGetInterestByIdQuery,
  useDeleteInterestMutation,
  useCreateInterestMutation,
  useUpdateInterestMutation,
} = dashboardApi
